/**
 * Cache key for `/flight/fetch-interstitial-page` responses.
 *
 * The key is derived from the request payload itself rather than from the
 * caller's props, so a request and its cache slot cannot drift apart: if two
 * opens build the same payload they are guaranteed the same key, and if they
 * build different payloads they are guaranteed different keys.
 *
 * `price` is deliberately excluded. It is part of the request but not part of
 * what the response is *about* — including it would evict the whole cache every
 * time the backend re-priced a fare, even though the same fare families come
 * back.
 */

export type InterstitialTripMode = 'ow' | 'rt' | 'srt'

interface SelectedSubTravelOption {
    subTravelOptionId?: string
    fareId?: string
}

interface SelectedTravelOption {
    travelOptionId?: string
    selectedSubTravelOptions?: SelectedSubTravelOption[]
}

/** The subset of the request body the key depends on. */
export interface InterstitialKeyPayload {
    dataId?: string
    selectedTravelOptions?: Record<string, SelectedTravelOption>
    isSpecialRT?: boolean
    cfwFlow?: boolean
    isPE?: boolean
    isWP?: boolean
    isMultiFare?: boolean
    international?: boolean
}

/**
 * FNV-1a. The inputs are long opaque backend strings (a `dataId` is ~400 chars
 * and a single fareId can exceed that), so they are folded down rather than
 * embedded — a key built from four raw fareIds would be several kilobytes and
 * would sit in every Redux action.
 */
const hash = (input: string): string => {
    let h = 0x811c9dc5
    for (let i = 0; i < input.length; i++) {
        h ^= input.charCodeAt(i)
        h = Math.imul(h, 0x01000193) >>> 0
    }
    return h.toString(16).padStart(8, '0')
}

export const getTripMode = (payload: InterstitialKeyPayload): InterstitialTripMode => {
    if (payload?.isSpecialRT) return 'srt'
    return Object.keys(payload?.selectedTravelOptions || {}).length > 1 ? 'rt' : 'ow'
}

/** Ordered travelOptionIds, J1 then J2 — leg order is meaningful. */
export const getTravelOptionIds = (payload: InterstitialKeyPayload): string[] =>
    Object.keys(payload?.selectedTravelOptions || {})
        .sort()
        .map(j => payload?.selectedTravelOptions?.[j]?.travelOptionId || '')

/**
 * Builds the cache key. Shape:
 *   `<mode>|<flags>|<travelOptionIds>|d<dataIdHash>|f<fareSelectionHash>`
 *
 * The mode and travelOptionIds are left readable because they are what anyone
 * debugging a stuck modal in Redux devtools actually needs to see.
 */
export const buildInterstitialCacheKey = (payload: InterstitialKeyPayload): string => {
    const mode = getTripMode(payload)
    const travelOptionIds = getTravelOptionIds(payload)

    // Response-shaping flags. Two opens that differ on any of these get
    // different responses from the backend and so must not share a slot.
    const flags = [
        payload?.cfwFlow ? 'c' : '-',
        payload?.isPE ? 'p' : '-',
        payload?.isWP ? 'w' : '-',
        payload?.isMultiFare ? 'm' : '-',
        payload?.international ? 'i' : '-',
    ].join('')

    // Which fares we are asking the backend about, per leg and in order.
    const fareSelection = Object.keys(payload?.selectedTravelOptions || {})
        .sort()
        .map(j => {
            const leg = payload?.selectedTravelOptions?.[j]
            const subs = (leg?.selectedSubTravelOptions || [])
                .map(s => `${s?.subTravelOptionId || ''}>${s?.fareId || ''}`)
                .join(',')
            return `${j}=${leg?.travelOptionId || ''}[${subs}]`
        })
        .join(';')

    return [
        mode,
        flags,
        travelOptionIds.join('+'),
        `d${hash(payload?.dataId || '')}`,
        `f${hash(fareSelection)}`,
    ].join('|')
}
